import { observer } from "mobx-react";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import Currency from "../../../../../../components/currency/currency";
import { TOOLTIP_PLACEMENTS } from "../../../../../../components/tooltip/tooltip-box";
import { TOOLTIP_VARIANTS, Tooltip } from "../../../../../../components/tooltip/tooltip";
import ProductCaseItemsBalanceStore from "../../../stores/product-case-items-balance-store";
import useDashboardTranslation from "../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import RmaNotZeroMoneyAmount from "../../rma-not-zero-money-amount/rma-not-zero-money-amount";
import ItemBalanceExchangeRangeAdjustment from "../../item-balance-exchange-range-adjustment/item-balance-exchange-range-adjustment";
import ItemBalanceRefundAdjustment from "../../item-balance-refund-adjustment/item-balance-refund-adjustment";
import "./rma-items-blalance.scss";
import dashboardTranslationsStore from "../../../../../../dashboard-translation/stores/dashboard-translations-store";

interface IProps {
    itemsBalanceStore: ProductCaseItemsBalanceStore;
    isHiddenResolution: boolean;
}

const RmaItemBalance: React.FC<IProps> = observer(({ itemsBalanceStore, isHiddenResolution }) => {
    const scope = 'dashboard-returns-rma-card';
    const { getTranslation } = useDashboardTranslation();

    if (isHiddenResolution) {
        return <Tooltip
            content={
                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                    {getTranslation('NO_POLICY_RULE_FOUND_NOTE', scope)}
                </FontBody2>
            }
            placement={TOOLTIP_PLACEMENTS.TOP}
            variant={TOOLTIP_VARIANTS.INFO}
        >
            <FontBody2
                color={FONT_COLOR.PRIMARY2}
                variant={FONT_VARIANT.BOLD}
            >
                {getTranslation('NO_APPROPRIATED_RULE_FOUND', scope)}
            </FontBody2>
        </Tooltip>
    }

    return (
        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
            {(!!itemsBalanceStore.balance && !dashboardTranslationsStore.isLoading) && (
                <>
                    {itemsBalanceStore.showBalanceBreakdown ? (
                        <Tooltip
                            content={
                                <div className="rma-item-balance-tooltip">
                                    <RmaNotZeroMoneyAmount
                                        title={getTranslation('RETURNED_ITEM', scope)}
                                        moneyDto={itemsBalanceStore.originalItemPaidPrice}
                                        color={FONT_COLOR.NEUTRAL_WHITE}
                                        useColon={true}
                                    />
                                    <div className="rma-item-balance-first-level-sublines">
                                        {itemsBalanceStore.isExchange && (
                                            <RmaNotZeroMoneyAmount
                                                title={getTranslation('PRICE_DIFFERENCE_ADJUSTMENTS', scope)}
                                                moneyDto={itemsBalanceStore.priceDifferenceAdjustments}
                                                useColon={true}
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                            />
                                        )}
                                        {itemsBalanceStore.exchangeRangeAdjustment && (
                                            <ItemBalanceExchangeRangeAdjustment
                                                exchangeRangeAdjustmentMoneyDto={itemsBalanceStore.exchangeRangeAdjustment}
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                                useColon={true}
                                            />
                                        )}
                                        {itemsBalanceStore.refundAdjustment && (
                                            <ItemBalanceRefundAdjustment
                                                refundAdjustmentMoneyDto={itemsBalanceStore.refundAdjustment}
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                                useColon={true}
                                            />
                                        )}
                                        <RmaNotZeroMoneyAmount
                                            title={getTranslation('BXGY_DISCOUNT_FEE', scope)}
                                            moneyDto={itemsBalanceStore.discountFee}
                                            color={FONT_COLOR.NEUTRAL_WHITE}
                                            useColon={true}
                                        />
                                    </div>
                                    {itemsBalanceStore.isExchange && itemsBalanceStore.newItemPaymentPrice?.amount !== 0 && (
                                        <>
                                            <RmaNotZeroMoneyAmount
                                                title={getTranslation('NEW_ITEM_PRICE_TITLE', scope)}
                                                moneyDto={itemsBalanceStore.newItemPaymentPrice}
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                                useColon={true}
                                            />
                                            <div className="rma-item-balance-sublines">
                                                <RmaNotZeroMoneyAmount
                                                    title={getTranslation('PRICE_DIFFERENCE_ADJUSTMENTS', scope)}
                                                    moneyDto={itemsBalanceStore.newItemPaymentPriceDifferenceAdjustments}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                />
                                            </div>
                                            <div className="rma-item-balance-sublines">
                                                <RmaNotZeroMoneyAmount
                                                    title={getTranslation('NEW_ITEM_BXGY_DISCOUNT_ADJUSTMENT', scope)}
                                                    moneyDto={itemsBalanceStore.newItemBxGyDiscountAdjustment}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                />
                                            </div>
                                            <div className="rma-item-balance-sublines">
                                                <RmaNotZeroMoneyAmount
                                                    title={
                                                        getTranslation('NEW_ITEM_APPLIED_DISCOUNT', scope,
                                                            itemsBalanceStore.newItemDiscounts
                                                                ? [itemsBalanceStore.newItemDiscounts.map((discount) => `"${discount}"`).join(', ')]
                                                                : []
                                                        )
                                                    }
                                                    moneyDto={itemsBalanceStore.newItemAppliedDiscount}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <RmaNotZeroMoneyAmount
                                        title={getTranslation('EXCHANGE_BONUS', scope)}
                                        moneyDto={itemsBalanceStore.switchToExchangeIncentive}
                                        color={FONT_COLOR.NEUTRAL_WHITE}
                                        useColon={true}
                                    />
                                    {itemsBalanceStore.showFeesBreakdown && (
                                        <>
                                            <FontBody2
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                            >
                                                {getTranslation('FEES', 'dashboard-create-rma')}
                                            </FontBody2>
                                            <div className="rma-item-balance-sublines">
                                                <RmaNotZeroMoneyAmount
                                                    title={getTranslation('RESTOCKING', scope)}
                                                    moneyDto={itemsBalanceStore.restockingFee}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                    useColon={true}
                                                />
                                                <RmaNotZeroMoneyAmount
                                                    title={getTranslation('RETURN', scope)}
                                                    moneyDto={itemsBalanceStore.rmaFee}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                    useColon={true}
                                                />
                                                <RmaNotZeroMoneyAmount
                                                    title={getTranslation('RETURN_LABEL', scope)}
                                                    moneyDto={itemsBalanceStore.returnLabelFee}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                    useColon={true}
                                                />
                                                <RmaNotZeroMoneyAmount
                                                    title={getTranslation('CANCELLATION', scope)}
                                                    moneyDto={itemsBalanceStore.cancellationFee}
                                                    color={FONT_COLOR.NEUTRAL_WHITE}
                                                    useColon={true}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {itemsBalanceStore.deductForOtherItems?.map(item =>
                                        <div key={item.otherItemProductCaseId}>
                                            <RmaNotZeroMoneyAmount
                                                title={`${getTranslation('DEDUCTED_FOR', scope)} ${item.otherItemName}`}
                                                moneyDto={item?.totalAmount}
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                                useColon={true}
                                            />
                                        </div>
                                    )}
                                    {itemsBalanceStore.paidByOtherItems?.map(item =>
                                        <div key={item.otherItemProductCaseId}>
                                            <RmaNotZeroMoneyAmount
                                                title={`${getTranslation('PAID_BY', scope)} ${item.otherItemName}`}
                                                moneyDto={item?.totalAmount}
                                                color={FONT_COLOR.NEUTRAL_WHITE}
                                                useColon={true}
                                            />
                                        </div>
                                    )}
                                </div>
                            }
                            placement={TOOLTIP_PLACEMENTS.TOP}
                            variant={TOOLTIP_VARIANTS.INFO}
                        >
                            <Currency
                                currencyCode={itemsBalanceStore.balance.currency_code}
                                amount={itemsBalanceStore.balance.amount}
                            />
                        </Tooltip>
                    ) :
                        <Currency
                            currencyCode={itemsBalanceStore.balance.currency_code}
                            amount={itemsBalanceStore.balance.amount}
                        />
                    }
                </>
            )}
        </FontBody2>
    );
});

export default RmaItemBalance;
